import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getStoredToken, clearUserSession } from "../utils/auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
// Messages the API's authorize middleware returns for a missing or revoked token, or a deactivated account (403)
const AUTH_FAILURE_MESSAGES = ["User not found", "Token missing", "Your account is no longer active"];

let checkedToken = null;
let tokenCheck = null;

// Checked once per token per page load; network errors are treated as valid so a flaky connection never logs anyone out
const isTokenAccepted = (token) => {
  if (checkedToken === token && tokenCheck) return tokenCheck;
  checkedToken = token;
  tokenCheck = fetch(`${API_BASE_URL}/user/profile`, { headers: { token } })
    .then(async (response) => {
      if (response.ok) return true;
      const body = await response.json().catch(() => ({}));
      return !([400, 401, 403].includes(response.status) && AUTH_FAILURE_MESSAGES.includes(body?.message));
    })
    .catch(() => true);
  return tokenCheck;
};

export default function ProtectedRoute({ children }) {
  const token = getStoredToken();
  const location = useLocation();
  // A Google/Facebook login result belongs to the login page; never render a protected page with an older stored token first
  const searchParams = new URLSearchParams(location.search);
  const isSocialLoginResult = searchParams.has("token") && searchParams.has("success");

  useEffect(() => {
    if (!token || isSocialLoginResult) return;
    let active = true;
    isTokenAccepted(token).then((accepted) => {
      if (active && !accepted) {
        clearUserSession();
        window.location.replace("/login");
      }
    });
    return () => {
      active = false;
    };
  }, [token, isSocialLoginResult]);

  if (isSocialLoginResult) {
    return <Navigate to={`/login${location.search}`} replace />;
  }

  if (!token) {
    // Redirect to login, save the current location for after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}
